import { useEffect, useState } from "react";
import { Box, Button } from "@material-ui/core";
import "./quickstart-banner.scss";

/*
 * onboarding-quickstart experiment — the TREATMENT surface (config-driven).
 *
 * The banner renders ONLY when the assigned experiment payload says
 * `quickstartEnabled: true` (see shouldShowQuickstart). The control payload is
 * `quickstartEnabled: false` — today's behavior — and getExperimentPayload
 * resolves control whenever the master experiment flag is off, the experiment
 * is dark, or there is no user id. So with the flag off this module renders
 * NOTHING and the page is byte-identical to today.
 *
 * One tap creates a first quiz via the SAME createQuiz action the form uses —
 * the page's existing success/error effects then navigate into the exam or
 * surface the error. No new API surface, no variant branches.
 *
 * Pre-ship UX guardrails (flip-readiness packet, #477/#480):
 * - The exam is an EXPLICIT student choice before anything is seeded: signup
 *   captures no exam type (register/ProfileComplete collect name/email/phone
 *   only), so no account exam is provably user-chosen — the banner asks. Only
 *   a one-exam catalog is auto-selected, since there is nothing to
 *   disambiguate; the copy and CTA always name the exam.
 * - One start surface at a time: the explicit "customize instead" action
 *   hands off to the form and dismisses the banner, so the banner's CTA and
 *   the form's Create button are never both the active path.
 *
 * v2 seed semantics (quickstart treatment finalization, PR #475):
 * - The seed sends ONLY the picked exam (`examType: [chosen]`), named after it.
 * - High-Yield is the seeded default where the exam defines it (the page's
 *   ABSITE-only threshold filter), mirroring the form's own auto-on rule.
 */

// Seed size. 5 (was 10 in #452): the daily free-tier allowance is tallied from
// questions in quizzes CREATED today (QLearn-service
// QuizService.findQuizQuestionByUserIdToday), so every seeded question is one
// the student cannot spend on a manual quiz until tomorrow — and a smaller
// seed is a smaller first-action commitment.
export const QUICKSTART_QUESTION_COUNT = 5;

// Cross-repo contract with QLearn-service's QuizDailyLimitGuard (service PR
// #475): a create marked with this source is VERIFIED server-side (first quiz
// ever + within the service's seed question cap) and then exempted from the
// free daily allowance — so the seeded first quiz stops spending the quota the
// comment above describes. The marker is a claim, not a key: an unverified
// claim just gets normal limit handling, so it cannot be forged into a quota
// bypass.
export const QUICKSTART_SEED_SOURCE = "QUICKSTART_SEED";

// Names the exam so the seeded quiz is recognizable in history ("My First
// ABSITE Quiz") and the post-seed surface itself shows which exam was chosen.
export const quickstartQuizName = (exam) => `My First ${exam} Quiz`;

// Mirrors the create-quiz form's defaults (create-quiz-page.jsx) so the seeded
// quiz is exactly what a user would get by picking this exam, selecting every
// discipline/organ-system, and pressing Create with the defaults untouched —
// including the form's rule that picking a High-Yield-capable exam turns the
// High-Yield filter on.
export const buildQuickstartQuizParams = ({
  userId,
  exam,
  highYield,
  discipline,
  organSystem,
  isTutor,
  tutorMode,
  timerMode,
}) => ({
  name: quickstartQuizName(exam),
  userId,
  source: QUICKSTART_SEED_SOURCE,
  quizOptions: {
    // Same rule as onCreateQuiz: tutor-mode only applies to tutor-role users.
    tutorOn: isTutor ? tutorMode : false,
    timerOn: timerMode,
    thresholdEnabled: highYield === true,
  },
  noOfQuestions: QUICKSTART_QUESTION_COUNT,
  questionOptions: {
    all: false,
    unused: true,
    correct: false,
    incorrect: false,
    marked: false,
  },
  categoryMap: {
    discipline: [...discipline],
    organSystem: [...organSystem],
    examType: [exam],
  },
});

/*
 * The full gate for rendering the quickstart treatment. Pure so it is
 * unit-testable: false unless the payload opts in AND this is genuinely a
 * first-run (history loaded and empty, no in-progress quiz) AND the category
 * data needed to seed the quiz has loaded.
 *
 * The in-progress check is two conditions on purpose: inProgressLoading
 * covers the window while the IN_PROGRESS fetch is in flight, and
 * quizProgressId === "" only holds once the page's derivation effect has
 * affirmatively resolved "no quiz in progress" (it stays undefined before
 * that effect runs, and when the fetch errored) — so the banner can never
 * flash for an in-progress-only user between fetch and derivation.
 */
export const shouldShowQuickstart = ({
  quickstartEnabled,
  historyLoading,
  overallQuizHistory,
  inProgressLoading,
  quizProgressId,
  examType,
  discipline,
  organSystem,
}) =>
  quickstartEnabled === true &&
  historyLoading === false &&
  Array.isArray(overallQuizHistory) &&
  overallQuizHistory.length === 0 &&
  inProgressLoading === false &&
  quizProgressId === "" &&
  Array.isArray(examType) && examType.length > 0 &&
  Array.isArray(discipline) && discipline.length > 0 &&
  Array.isArray(organSystem) && organSystem.length > 0;

export default function QuickstartBanner({
  onStart,
  onCustomize,
  onExamPick,
  loading,
  examTypes,
  highYieldExamTypes = [],
}) {
  const catalog = Array.isArray(examTypes) ? examTypes : [];
  // Explicit-choice guardrail: pre-select only when there is exactly one exam
  // in the catalog — nothing to disambiguate, and the copy still names it.
  const [selectedExam, setSelectedExam] = useState(
    catalog.length === 1 ? catalog[0] : null
  );

  // Mirror every pick (chip tap AND auto-preselect/catalog re-derive) to the
  // page, so the form beside the banner shows the same exam instead of a red
  // "nothing selected" contradiction. Effect, not click-handler: the
  // single-exam auto-select and the catalog-change reset must sync too.
  useEffect(() => {
    if (selectedExam && typeof onExamPick === 'function') {
      onExamPick(selectedExam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExam]);

  // Re-derive the selection if the catalog's CONTENT ever changes while
  // mounted (today it is fetched once, but a stale pick must never survive a
  // catalog it no longer belongs to). Keyed on content, not array identity —
  // the page may hand down a fresh array reference on unrelated re-renders.
  // NUL-joined: exam names can contain spaces (cf. "General Surgery").
  const catalogKey = catalog.join("\u0000");
  useEffect(() => {
    const exams = catalogKey === "" ? [] : catalogKey.split("\u0000");
    setSelectedExam((prev) => {
      if (exams.length === 1) return exams[0];
      return prev && exams.includes(prev) ? prev : null;
    });
  }, [catalogKey]);

  // Whether the picked exam's seed runs the High-Yield filter (the copy must
  // say so — the seed should never be quietly narrower than advertised).
  const highYield = !!selectedExam && highYieldExamTypes.includes(selectedExam);

  const startLabel = selectedExam
    ? `Start my first ${selectedExam} quiz`
    : "Start my first quiz";

  return (
    <Box className="quickstart-banner" data-testid="quickstart-banner">
      <Box className="quickstart-banner-text">
        <h3>New here? Start your first quiz in one tap.</h3>
        <p>
          {selectedExam
            ? `We'll set up a ${QUICKSTART_QUESTION_COUNT}-question ${highYield ? "high-yield " : ""}${selectedExam} quiz across all its topics, from questions you haven't seen yet.`
            : `Pick your exam and we'll set up a ${QUICKSTART_QUESTION_COUNT}-question quiz across all its topics, from questions you haven't seen yet.`}
        </p>
        {catalog.length > 1 && (
          <Box
            className="quickstart-banner-exams"
            role="radiogroup"
            aria-label="Pick your exam"
          >
            {catalog.map((exam) => (
              <Button
                key={exam}
                size="small"
                variant={exam === selectedExam ? "contained" : "outlined"}
                color="primary"
                className="quickstart-banner-exam"
                data-testid={`quickstart-exam-${exam}`}
                role="radio"
                aria-checked={exam === selectedExam}
                onClick={() => setSelectedExam(exam)}
              >
                {exam}
              </Button>
            ))}
          </Box>
        )}
        <p className="quickstart-banner-note">
          Nothing is locked in — you can end the quiz or change anything later.
        </p>
      </Box>
      <Box className="quickstart-banner-actions">
        <Button
          variant="contained"
          color="primary"
          className="quickstart-banner-cta"
          data-testid="quickstart-start-btn"
          disabled={!!loading || !selectedExam}
          onClick={() => onStart(selectedExam)}
        >
          {loading ? "Starting…" : startLabel}
        </Button>
        <Button
          className="quickstart-banner-customize"
          data-testid="quickstart-customize-btn"
          disabled={!!loading}
          onClick={onCustomize}
        >
          I&apos;ll customize instead
        </Button>
      </Box>
    </Box>
  );
}
